/**
 * Service Desk — single source of truth for all API endpoint paths.
 *
 * Rules:
 *  • No trailing slash on base constants (added at call-site via helpers).
 *  • Helper functions return the full path string for parameterised URLs.
 *  • Import in service files: `import { SD_ENDPOINTS as EP } from '@sd/services/endpoints'`
 */

// ---------------------------------------------------------------------------
// Base prefixes
// ---------------------------------------------------------------------------
const SD    = "/service-desk";    // new REST base (e.g. /service-desk/tickets/)
const SD_V1 = "/service_desk";    // legacy snake_case base (existing endpoints)
const FORMILY = "/formily/dynamic-model";
const AUTH    = "/auth";
const PTS     = "/pts_app";

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const SD_ENDPOINTS = {
  auth: {
    login:       `${AUTH}/login/`,
    logout:      `${AUTH}/logout/`,
    signup:      `${AUTH}/`,
    currentRole: `${SD_V1}/user-current-role/`,
  },

  // -------------------------------------------------------------------------
  // Tickets (legacy ticket_create resource — primary write/read endpoint)
  // -------------------------------------------------------------------------
  tickets: {
    base:              `${SD_V1}/ticket_create/`,
    detail:  (id: string) => `${SD_V1}/ticket_create/${encodeURIComponent(id)}/`,
    bulkUpdate:        `${SD_V1}/ticket_bulk_update/`,
    // REST-style (newer)
    restBase:          `${SD}/tickets/`,
    restDetail: (id: string) => `${SD}/tickets/${encodeURIComponent(id)}/`,
  },

  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------
  dashboard: {
    main:            `${SD_V1}/main-dashboard/`,
    statusDashboard: `${SD_V1}/status_dashboard/`,
    agentStatus:     `${SD_V1}/agent_status/`,
  },

  // -------------------------------------------------------------------------
  // Activity logs
  // -------------------------------------------------------------------------
  activityLogs: {
    base: `${SD_V1}/activity-logs/`,
  },

  // -------------------------------------------------------------------------
  // Ticket history & notes
  // -------------------------------------------------------------------------
  ticketHistory: {
    base: `${SD_V1}/ticket_history/`,
  },
  ticketNotes: {
    base: `${SD_V1}/ticket_notes/`,
  },

  // -------------------------------------------------------------------------
  // Employees / Agents
  // -------------------------------------------------------------------------
  employees: {
    list:      `${SD_V1}/employee_list/`,
    agentList: `${SD_V1}/agent_employee_list/`,
    projectAssigned: `${SD_V1}/project-assigned/`,
  },

  // -------------------------------------------------------------------------
  // Reports
  // -------------------------------------------------------------------------
  reports: {
    tracker: `${SD_V1}/reports/`,
    summary: `${SD}/reports/summary/`,
    tickets: `${SD}/reports/tickets/`,
  },

  // -------------------------------------------------------------------------
  // Kanban
  // -------------------------------------------------------------------------
  kanban: {
    view: `${SD_V1}/kanban-view/`,
  },

  // -------------------------------------------------------------------------
  // Assets
  // -------------------------------------------------------------------------
  assets: {
    base:           `${SD}/assets/`,
    detail: (id: string) => `${SD}/assets/${encodeURIComponent(id)}/`,
  },

  // -------------------------------------------------------------------------
  // Knowledge Base
  // -------------------------------------------------------------------------
  knowledgeBase: {
    base:           `${SD}/knowledge-base/`,
    detail: (id: string) => `${SD}/knowledge-base/${encodeURIComponent(id)}/`,
  },

  // -------------------------------------------------------------------------
  // Users / Roles
  // -------------------------------------------------------------------------
  users: {
    base:           `${SD}/users/`,
    detail: (id: string) => `${SD}/users/${encodeURIComponent(id)}/`,
  },
  roles: {
    list: `/tenant-permissions/roles/`,
  },
  priorities: {
    list: `${SD_V1}/priority_listing/`,
  },

  // -------------------------------------------------------------------------
  // Merge tickets
  // -------------------------------------------------------------------------
  mergeTickets: {
    base: `${SD_V1}/merge_tickets_list/`,
  },

  // -------------------------------------------------------------------------
  // In-app notifications
  // -------------------------------------------------------------------------
  inAppNotifications: {
    base:           `/in-app-notifications/`,
    read:   (id: string) => `/in-app-notifications/${encodeURIComponent(id)}/read/`,
    markAllRead:    `/in-app-notifications/mark-all-read/`,
  },

  // -------------------------------------------------------------------------
  // Formily dynamic-model
  // -------------------------------------------------------------------------
  formily: {
    base:             FORMILY,
    gridData: (model: string) =>
      `${FORMILY}/${encodeURIComponent(model.trim())}/grid_data/`,
    record: (model: string, id?: string) => {
      const base = `${FORMILY}/${encodeURIComponent(model.trim())}`;
      return id ? `${base}/${encodeURIComponent(id)}/` : `${base}/`;
    },
  },

  // -------------------------------------------------------------------------
  // PTS integration
  // -------------------------------------------------------------------------
  pts: {
    openProjectList: `${PTS}/project-summary/open-project-list/`,
    taskList:        `${PTS}/project-task/`,
    workTypeList:    `/dropdown/PTS%20Work%20Type/`,
    createTask:      `${SD_V1}/pts_task_create/`,
  },

  // -------------------------------------------------------------------------
  // AI Chat (Chatboard)
  // -------------------------------------------------------------------------
  chat: {
    message: `${SD_V1}/chat/`,
  },
} as const;
